import React, { useState, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isToday } from 'date-fns';
import helpIcon from '../../assets/icon/ic_help.png';
import arrowIcon from '../../assets/icon/open_arrow.svg';
import arrowIcon1 from '../../assets/icon/close_arrow.svg';
import { useLanguage } from '../../hooks/useLanguage';

const Calendar = ({ stockCalendar = {}, onMonthChange }) => {
    const { t } = useLanguage();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [showHelp, setShowHelp] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showFullCalendar, setShowFullCalendar] = useState(false);
    
    // stockCalendar props를 사용하도록 수정
    const stockStatus = stockCalendar || {};

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const handlePrevMonth = () => {
        const newDate = subMonths(currentDate, 1);
        setCurrentDate(newDate);
        // 부모 컴포넌트에 월 변경 알림
        if (onMonthChange) {
            onMonthChange(newDate);
        }
    };

    const handleNextMonth = () => {
        const newDate = addMonths(currentDate, 1);
        setCurrentDate(newDate);
        // 부모 컴포넌트에 월 변경 알림
        if (onMonthChange) {
            onMonthChange(newDate);
        }
    };

    const getDayClass = (day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        let classes = 'day';
        
        // 오늘 날짜보다 이전 날짜는 disabled 클래스 추가
        if (day < new Date(new Date().setHours(0, 0, 0, 0))) {
            classes += ' disabled';
        }
        else if (stockStatus[dateStr]) {
            classes += ` ${stockStatus[dateStr]}`;
        }
        
        return classes;
    };
    
    const getDateClass = (day) => {
        let classes = 'date';
        if (isToday(day)) {
            classes += ' today';
        }
        return classes;
    };

    // 각 날짜의 상태(재고)를 반환하는 함수 수정
    const getStockDotClass = (day) => {
        // 오늘 날짜보다 이전 날짜는 비활성화된 점 표시
        if (day < new Date(new Date().setHours(0, 0, 0, 0))) {
            return 'stock-dot disabled';
        }
        
        const dateStr = format(day, 'yyyy-MM-dd');
        if (stockStatus[dateStr]) {
            return `stock-dot ${stockStatus[dateStr]}`;
        }
        
        // stockCalendar에 데이터가 없는 경우 기본값 처리
        return 'stock-dot gray'; // 또는 'stock-dot red' 등 원하는 기본값
    };

    const getDays = () => {
        const today = new Date();
        if (!showFullCalendar) {
            // 현재 선택된 달과 오늘의 달이 같은지 확인
            const isCurrentMonth = 
                today.getMonth() === currentDate.getMonth() && 
                today.getFullYear() === currentDate.getFullYear();
            
            if (isCurrentMonth) {
                // 오늘 날짜가 포함된 2주 표시 (오늘부터 시작하는 것이 아닌, 오늘이 포함된 주의 월요일부터)
                const todayDay = today.getDay(); // 오늘의 요일 (0: 일요일, 1: 월요일, ..., 6: 토요일)
                const diff = todayDay === 1 ? 0 : todayDay === 0 ? -6 : 1 - todayDay; // 월요일로 조정
                
                const mondayOfThisWeek = new Date(today);
                mondayOfThisWeek.setDate(today.getDate() + diff);
                
                const endOfTwoWeeks = new Date(mondayOfThisWeek);
                endOfTwoWeeks.setDate(mondayOfThisWeek.getDate() + 13); // 2주 (14일)
                
                return eachDayOfInterval({ start: mondayOfThisWeek, end: endOfTwoWeeks });
            } else {
                // 다른 달인 경우, 월의 첫째 주 월요일부터 2주 표시
                const firstDayOfMonth = startOfMonth(currentDate);
                const dayOfWeek = firstDayOfMonth.getDay();
                
                const startDate = new Date(firstDayOfMonth);
                const diff = dayOfWeek === 1 ? 0 : dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                startDate.setDate(firstDayOfMonth.getDate() + diff);
                
                const endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + 13);
                
                return eachDayOfInterval({ start: startDate, end: endDate });
            }
        } else {
            // 월 전체 달력 표시 - 현재 달의 날짜만 표시하도록 수정
            const start = startOfMonth(currentDate);
            const end = endOfMonth(currentDate);
            
            return eachDayOfInterval({ start, end });
        }
    };

    // 달력을 주 단위로 그룹화하는 함수
    const groupByWeek = (days) => {
        const weeks = [];
        let week = [];
        
        days.forEach((day, index) => {
            week.push(day);
            if ((index + 1) % 7 === 0) {
                weeks.push(week);
                week = [];
            }
        });
        
        if (week.length > 0) {
            weeks.push(week);
        }
        
        return weeks;
    };

    const weeks = groupByWeek(getDays());

    return (
        <div className="calendar-container">
            <div className="calendar-header">
                <div className="month-nav">
                    <button
                        className='prev'
                        onClick={handlePrevMonth}>
                    </button>
                    <h2>{format(currentDate, 'yyyy. MM')}</h2>
                    <button 
                        className='next'
                        onClick={handleNextMonth}>
                    </button>
                </div>
                <div className="stock-info">
                    <div 
                        className="help-icon"
                        onMouseEnter={() => !isMobile && setShowHelp(true)}
                        onMouseLeave={() => !isMobile && setShowHelp(false)}
                        onClick={() => isMobile && setShowHelp(!showHelp)}
                    >
                        <img src={helpIcon} alt="도움말" loading="lazy"/>
                    </div>
                    {showHelp && (
                        <div className="stock-status-tooltip">
                            <div className="tooltip-item">
                                <span className="dot red"></span>
                                <span>{t('stock_shortage')}</span>
                            </div>
                            <div className="tooltip-item">
                                <span className="dot yellow"></span>
                                <span>{t('stock_quantity')}</span>
                            </div>
                            <div className="tooltip-item">
                                <span className="dot green"></span>
                                <span>{t('stock_surplus')}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="calendar-grid">
                <div className="weekdays">
                    <div>{t('mon')}</div>
                    <div>{t('tue')}</div>
                    <div>{t('wed')}</div>
                    <div>{t('thu')}</div>
                    <div>{t('fri')}</div>
                    <div>{t('sat')}</div>
                    <div>{t('sun')}</div>
                </div>
                
                <div className="days">
                    {showFullCalendar ? (
                        // 전체 달력 표시 - 현재 달만 표시하도록 수정
                        <>
                            {/* 첫 주 시작 전 빈 셀 추가 */}
                            {Array.from({ length: (startOfMonth(currentDate).getDay() + 6) % 7 }).map((_, i) => (
                                <div key={`empty-start-${i}`} className="day empty"></div>
                            ))}
                            
                            {/* 현재 달의 날짜 표시 */}
                            {eachDayOfInterval({ 
                                start: startOfMonth(currentDate), 
                                end: endOfMonth(currentDate) 
                            }).map(day => (
                                <div key={day.toString()} className={getDayClass(day)}>
                                    <span className={getDateClass(day)}>{format(day, 'd')}</span>
                                    <span className={getStockDotClass(day)}></span>
                                </div>
                            ))}
                            
                            {/* 마지막 주 이후 빈 셀 추가 */}
                            {Array.from({ 
                                length: (7 - endOfMonth(currentDate).getDay()) % 7
                            }).map((_, i) => (
                                <div key={`empty-end-${i}`} className="day empty"></div>
                            ))}
                        </>
                    ) : (
                        // 2주 달력 표시 (기존 코드 유지)
                        weeks.slice(0, 2).flatMap(week => 
                            week.map((day) => (
                                <div key={day.toString()} className={getDayClass(day)}>
                                    <span className={getDateClass(day)}>{format(day, 'd')}</span>
                                    <span className={getStockDotClass(day)}></span>
                                </div>
                            ))
                        )
                    )}
                </div>
            </div>
            
            <button 
                className={`show-more-button ${showFullCalendar ? 'expanded' : ''}`}
                onClick={() => setShowFullCalendar(!showFullCalendar)}
            >
                {showFullCalendar ? (
                    <img src={arrowIcon1} alt="접기" loading="lazy"/>
                ) : (
                    <img src={arrowIcon} alt="더보기" loading="lazy"/>
                )}
            </button>
        </div>
    );
};

export default Calendar;